import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  CheckCircle, XCircle, Loader2, Mail, ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmailChangePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [status, setStatus] = useState('verifying'); // verifying, success, partial, error
  const [message, setMessage] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [verifiedType, setVerifiedType] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    const type = searchParams.get('type');
    
    if (!token || !type) {
      setStatus('error');
      setMessage('Invalid verification link. Missing token or type.');
      return;
    }
    
    verifyEmail(token, type);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyEmail = async (token, type) => {
    try {
      const response = await fetch(`${API_URL}/api/portal/verify-email-change`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: token,
          email_type: type
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        setStatus('error');
        setMessage(data.detail || 'Verification failed');
        return;
      }
      
      if (data.completed) {
        setStatus('success');
        setMessage(data.message);
        setNewEmail(data.new_email);
      } else {
        setStatus('partial');
        setMessage(data.message);
        setVerifiedType(data.verified);
      }
      
    } catch (err) {
      console.error('Verification error:', err);
      setStatus('error');
      setMessage('Failed to verify email. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E8EEFF] to-white flex items-center justify-center p-4">
      <Card className="max-w-md w-full shadow-2xl border-0 rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#3B5BDB] to-[#5B7FFF] p-6 text-center">
          <a href="https://losspay.com">
            <img 
              src="https://images.losspay.com/losspay/losspay-logo-white.png" 
              alt="LossPay" 
              className="h-10 mx-auto mb-4"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </a>
          <h1 className="text-xl font-bold text-white">Email Verification</h1>
        </div>
        
        <CardContent className="p-8">
          {/* Verifying State */}
          {status === 'verifying' && (
            <div className="text-center py-8">
              <Loader2 className="h-12 w-12 animate-spin text-[#3B5BDB] mx-auto mb-4" />
              <p className="text-slate-600 font-medium">Verifying your email...</p>
            </div>
          )}
          
          {/* Success - Both Emails Verified */}
          {status === 'success' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Email Changed Successfully!</h2>
              <p className="text-slate-600 mb-6">{message}</p>
              
              {newEmail && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
                  <p className="text-sm text-green-700">
                    <strong>New email:</strong> {newEmail}
                  </p>
                </div>
              )}
              
              <Button
                onClick={() => navigate('/portal/login')}
                className="w-full bg-[#3B5BDB] hover:bg-[#2F4BC7] text-white"
                data-testid="login-btn"
              >
                Sign In with New Email
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          )}
          
          {/* Partial - One Email Verified */}
          {status === 'partial' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="h-8 w-8 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Almost There!</h2>
              <p className="text-slate-600 mb-6">{message}</p>
              
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-blue-700">
                  <CheckCircle className="h-4 w-4 inline mr-1" />
                  <strong>{verifiedType === 'old' ? 'Current' : 'New'} email verified</strong>
                </p>
                <p className="text-sm text-blue-600 mt-1">
                  Check your {verifiedType === 'old' ? 'new' : 'current'} email inbox for the other verification link.
                </p>
              </div>
              
              <Button
                variant="outline"
                onClick={() => navigate('/portal/login')}
                className="w-full"
              >
                Return to Sign In
              </Button>
            </div>
          )}
          
          {/* Error State */}
          {status === 'error' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Verification Failed</h2>
              <p className="text-slate-600 mb-6">{message}</p>
              
              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/settings')}
                  className="w-full bg-[#3B5BDB] hover:bg-[#2F4BC7] text-white"
                >
                  Go to Settings
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/portal/login')}
                  className="w-full"
                >
                  Return to Sign In
                </Button>
              </div>
            </div>
          )}
        </CardContent>
        
        {/* Footer */}
        <div className="px-8 pb-6 text-center">
          <p className="text-xs text-slate-500">
            Need help? Contact <a href="mailto:support@losspay.com" className="text-[#3B5BDB] hover:underline">support@losspay.com</a>
          </p>
        </div>
      </Card>
    </div>
  );
}
